/**
 * BE-186 — a quiz's resume point on the account, replacing the `localStorage` steps of the
 * sizes / style quizzes. Behind the `quiz_progress` capability: off → nothing is read or written
 * (the quiz starts from Welcome). Reads answer `null` when nothing is saved (404) or the call
 * fails — a lost resume point is not an error the user needs to see; writes are fire-and-forget
 * and only logged.
 */
import { hasSurveysApi } from '@/features/survey/flags'
import {
  deleteQuizProgress,
  getQuizProgress,
  putQuizProgress,
  type QuizProgress,
  type QuizProgressKind,
} from '@/features/survey/api/quiz-progress'

export interface UseQuizProgress<Draft extends Record<string, unknown>> {
  load: () => Promise<QuizProgress<Draft> | null>
  save: (step: string | null, answersDraft: Draft) => Promise<void>
  clear: () => Promise<void>
}

export function useQuizProgress<Draft extends Record<string, unknown> = Record<string, unknown>>(
  kind: QuizProgressKind,
): UseQuizProgress<Draft> {
  const enabled = () => hasSurveysApi('quiz_progress')

  async function load(): Promise<QuizProgress<Draft> | null> {
    if (!enabled()) return null
    try {
      const response = await getQuizProgress<Draft>(kind)
      return response.data
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 404) console.warn(`Quiz progress (${kind}): not read`, error)
      return null
    }
  }

  async function save(step: string | null, answersDraft: Draft): Promise<void> {
    if (!enabled()) return
    try {
      await putQuizProgress<Draft>(kind, { step, answersDraft })
    } catch (error) {
      console.warn(`Quiz progress (${kind}): not saved`, error)
    }
  }

  async function clear(): Promise<void> {
    if (!enabled()) return
    try {
      await deleteQuizProgress(kind)
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 404) console.warn(`Quiz progress (${kind}): not cleared`, error)
    }
  }

  return { load, save, clear }
}
